import { useEffect, useState, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { CoreBox, CoreTooltip, CoreTypography } from '@youscience/core';
import { Major, MajorCardData, RecommendationChipData, searchFilters } from '@common-types';

import { getMajorDetailsPath, getSchoolsByMajorPath } from '@constants/ROUTES';
import { COLLEGES_IMAGES_URL } from '@constants/externalRoutes';
import { RECOMMENDATION_CHIPS_DATA } from '@constants/major';

import { EllipsisTypography } from '@components/EllipsisTypography';
import ActionButton from '@components/ActionButton';

import { CollegeLocation } from '@interfaces/location';

import useFavoriteMajorsStore from '@store/FavoriteMajorsStore';
import useHomePageStore from '@store/HomePageStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';

import { getFromMajorLocation } from '@utils/breadcrumbs';

import { MajorSurveyModalProps, withMajorSurveyModal } from '@HOCs/withMajorSurveyModal';

import NotesIcon from '@images/notes.svg?react';
import HeartIcon from '@images/heart.svg?react';
import FilledHeartIcon from '@images/heart-filled.svg?react';
import ChevronRight from '@images/chevron-right.svg?react';
import LocationIcon from '@images/location.svg?react';

import {
  sxStyles,
  StyledMajorCardWrap,
  StyledPersonalRecommendation,
  StyledOverallFitWrap,
  StyledTitlesWrap,
  StyledMajorPhoto,
  StyledUsheCollegesCount,
  StyledUsheLocationIcon,
} from './MajorCard.styles';
import { getMajorImageSrc } from '@utils/major';
import { useStateSpotlight } from '@hooks/useStateSpotlight';

export const renderPersonalRecommendationChips = (
  majorData: Partial<MajorCardData>,
  renderItem: (chipData: RecommendationChipData, isRenderText: boolean) => ReactNode,
) => {
  return RECOMMENDATION_CHIPS_DATA.filter(
    (item: RecommendationChipData) => (majorData as Record<string, Array<unknown>>)[item.type],
  ).map((item: RecommendationChipData, index: number, array: RecommendationChipData[]) => {
    return renderItem(item, array.length <= 1);
  });
};

const renderPersonalRecommendationItem = (chipData: RecommendationChipData, isRenderText: boolean) => {
  const { type, icon, label, color } = chipData;

  return (
    <StyledPersonalRecommendation key={label} color={color}>
      {isRenderText ? (
        <CoreBox data-testid={type}>{icon}</CoreBox>
      ) : (
        <CoreTooltip
          title={(searchFilters.personalRecommendation.fieldLabels as Record<string, unknown>)[type] as string}
        >
          <CoreBox data-testid={type}>{icon}</CoreBox>
        </CoreTooltip>
      )}

      {isRenderText && (
        <CoreTypography component="p" variant="caption">
          {label}
        </CoreTypography>
      )}
    </StyledPersonalRecommendation>
  );
};

const getUsheCollegesCountText = (count: number) => {
  if (count === 0) {
    return 'Not offered at Utah public colleges';
  }

  return count === 1 ? '1 at a Utah public college' : `${count} at Utah public colleges`;
};

const renderUsheCollegesCount = (count: number) => {
  const isOffered = count > 0;

  return (
    <StyledUsheCollegesCount isOffered={isOffered}>
      {isOffered && (
        <StyledUsheLocationIcon aria-hidden="true">
          <LocationIcon />
        </StyledUsheLocationIcon>
      )}

      {getUsheCollegesCountText(count)}
    </StyledUsheCollegesCount>
  );
};

interface MajorCardProps extends MajorSurveyModalProps {
  major: Major;
  componentId?: string;
  isShowImage?: boolean;
  successDialog?: { openSuccessDialog: () => void; closeSuccessDialog: () => void };
}

// TODO Temporary decision. Then replace it on MajorCard component from component-library
const MajorCard = (props: MajorCardProps) => {
  const {
    componentId,
    major,
    major: { cipCode, collegesCount, usheCollegesCount: rawUsheCollegesCount },
    isShowImage,
    onOpenSurvey,
  } = props;

  const navigate = useNavigate();
  const location: CollegeLocation = useLocation();
  const { spotlight } = useStateSpotlight();
  const usheCollegesCount = spotlight ? rawUsheCollegesCount : undefined;

  const [isFavoriteMajor, setIsFavoriteMajor] = useState(false);

  const { cipCodes: favoriteMajorsCipCodes } = useUserPreferencesStore((state) => state.majorFavorites);
  const toggleFavoriteMajors = useFavoriteMajorsStore((state) => state.toggleFavoriteMajors);
  const landingPageSchoolData = useHomePageStore((state) => state.landingPageSchoolData);
  const isLandingPage = !!landingPageSchoolData?.id;
  const addToFavoritesButtonId = componentId ? `${componentId}-add-to-favorites-button` : 'add-to-favorites-button';
  const areaCipCode = Math.floor(+cipCode);
  const areaImageSrc = getMajorImageSrc(areaCipCode);

  const collegeLink = isLandingPage ? null : (
    <Link
      to={getSchoolsByMajorPath(cipCode)}
      data-testid="collegesOfferingMajors"
      state={getFromMajorLocation(location.pathname)}
      key={cipCode}
    >
      <CoreTypography component="div" variant="body2" sx={sxStyles.schoolLink}>
        {`${collegesCount} colleges offer this program`}
      </CoreTypography>
    </Link>
  );

  const majorCardData: MajorCardData = {
    ...major,
    areaOfStudyTitle: major.areaOfStudyTitle,
    isFavorite: isFavoriteMajor,
    moreDetailsUrl: '',
    favoriteUrl: '',
    imageUrl: COLLEGES_IMAGES_URL,
  };

  const handleOpenSurvey = () => {
    onOpenSurvey?.(cipCode);
  };

  useEffect(() => {
    const isCipCodeInFavoriteMajors = !!favoriteMajorsCipCodes?.find(
      (favoriteCipCode: string) => favoriteCipCode === cipCode,
    );

    setIsFavoriteMajor(isCipCodeInFavoriteMajors);
  }, [favoriteMajorsCipCodes, cipCode]);

  const onShowNotesClick = () => {
    onOpenSurvey?.(major.cipCode);
  };

  const onMoreDetailsClick = () => {
    navigate(getMajorDetailsPath(cipCode), { state: getFromMajorLocation(location.pathname) });
  };

  const onFavoriteClick = () => {
    if (!isFavoriteMajor) {
      handleOpenSurvey();
    }

    void toggleFavoriteMajors(cipCode);
  };

  return (
    <StyledMajorCardWrap data-testid="majorCard">
      {isShowImage && (
        <StyledMajorPhoto data-testid="defaultMajorPhoto">
          <img src={areaImageSrc} alt="Major" width="auto" height="auto" decoding="async" loading="lazy" />
        </StyledMajorPhoto>
      )}

      <StyledOverallFitWrap>
        {renderPersonalRecommendationChips(majorCardData, renderPersonalRecommendationItem)}
      </StyledOverallFitWrap>

      <CoreBox sx={sxStyles.contentWrap}>
        <StyledTitlesWrap>
          {/* FIXME: icon breaks UI - use MajorCard from component-library/prism and refactor where it's imported*/}
          {/* <CoreBox data-testid="areaIcon">
            <StyledAreaIcon imageSrc={areaIconSrc} />
          </CoreBox> */}

          <CoreTooltip title={majorCardData.areaOfStudyTitle} disableInteractive>
            <EllipsisTypography sx={sxStyles.areaOfStudyTitle} stringCount={2}>
              {majorCardData.areaOfStudyTitle}
            </EllipsisTypography>
          </CoreTooltip>

          <EllipsisTypography data-testid="majorTitle" stringCount={2} variant="h6" component="h5">
            {majorCardData.cipTitle}
          </EllipsisTypography>

          <CoreBox sx={sxStyles.collegeOffer}>
            {typeof usheCollegesCount === 'number' ? (
              <CoreBox sx={sxStyles.collegeOfferRow}>
                {collegeLink}
                {collegeLink && <span aria-hidden="true">{'\u00B7'}</span>}
                {renderUsheCollegesCount(usheCollegesCount)}
              </CoreBox>
            ) : (
              collegeLink
            )}
          </CoreBox>
        </StyledTitlesWrap>

        <CoreBox sx={sxStyles.actionsWrap}>
          <CoreBox sx={sxStyles.favorite}>
            <ActionButton data-testid={addToFavoritesButtonId} variant="text" size="large" onClick={onFavoriteClick}>
              {majorCardData.isFavorite ? (
                <FilledHeartIcon data-testid="filled" />
              ) : (
                <HeartIcon data-testid="notFilled" />
              )}
            </ActionButton>

            {majorCardData.isFavorite && (
              <ActionButton data-testid="notesIcon" variant="text" size="large" onClick={onShowNotesClick}>
                <NotesIcon />
              </ActionButton>
            )}
          </CoreBox>

          <CoreBox sx={sxStyles.divider} />

          <CoreBox sx={sxStyles.moreWrap}>
            <ActionButton sx={sxStyles.moreIcon} size="large" variant="text" onClick={onMoreDetailsClick}>
              More
              <ChevronRight />
            </ActionButton>
          </CoreBox>
        </CoreBox>
      </CoreBox>
    </StyledMajorCardWrap>
  );
};

export const MajorCardWithModal = withMajorSurveyModal(MajorCard);
